// OWMap BSL1 — Brew Flow
// Complete Brew experience: param config → waiting → reveal → annotation.
// Mounts over the main app. Globe integration via globeRef prop.
// Log prefix: [BrewFlow]

const {
  useState: useBF, useEffect: useEBF, useCallback: useCBF,
  useRef: useRBF, useMemo: useMBF,
} = React;

// ── Constants mirrored from brew-constants.ts ─────────────────────────────────
const STORY_TYPES = [
  { id: 'FOLK',  label: '民间故事', icon: '🌿' },
  { id: 'MYTH',  label: '神话传说', icon: '✦'  },
  { id: 'HIST',  label: '历史叙事', icon: '⏳' },
  { id: 'NAT',   label: '自然奇谈', icon: '🌊' },
  { id: 'DREAM', label: '梦境故事', icon: '◎'  },
];
const AUDIENCE_STYLES = [
  { id: 'CHILD',   label: '儿童向' },
  { id: 'ADULT',   label: '成人向' },
  { id: 'SCHOLAR', label: '学者向' },
];
const LANGS = [
  { id: 'zh', label: '中文' },
  { id: 'en', label: 'English' },
];
const STAGE_LABELS = {
  queued:      { stage: 1, total: 5, hint: '任务排队中…' },
  discovering: { stage: 2, total: 5, hint: '正在挖掘这片土地的记忆…' },
  mining:      { stage: 3, total: 5, hint: '收集文化内容素材…' },
  generating:  { stage: 4, total: 5, hint: '正在酿造你的故事…' },
  review:      { stage: 5, total: 5, hint: '内容自动审核中…' },
};
// Ambient hints cycle during each stage
const STAGE_AMBIENT = {
  discovering: [
    '正在查阅文化地图层…',
    '发现历史坐标节点…',
    '匹配地理文化特征…',
  ],
  mining: [
    '挖掘地方传说与史料…',
    '构建文化内容素材库…',
    '对照 10,000+ 故事样本…',
  ],
  generating: [
    '找到了最合适的故事视角…',
    '正在撰写…',
    '为你的故事寻找最好的声音…',
  ],
  review: [
    '内容质量六维评审中…',
    '验证地理信息一致性…',
    '即将完成…',
  ],
};

// ── Step 1: Param Config Panel ────────────────────────────────────────────────
function BrewParamPanel({ mode, coordInfo, userTier, onSubmit, onCancel }) {
  const [storyType,    setStoryType]    = useBF('HIST');
  const [audience,     setAudience]     = useBF('ADULT');
  const [lang,         setLang]         = useBF('zh');
  const [tags,         setTags]         = useBF([]);
  const [tagInput,     setTagInput]     = useBF('');
  const [brewerNote,   setBrewerNote]   = useBF('');
  const [visibility,   setVisibility]   = useBF('public');
  const [showAdvanced, setShowAdvanced] = useBF(false);
  const [isVhm,        setIsVhm]        = useBF(false);
  const [submitting,   setSubmitting]   = useBF(false);

  const canVhm    = userTier === 'P2' || userTier === 'X';
  const isSimple  = userTier === 'F' || userTier === 'I';
  const brewCost  = isVhm ? 3 : 1;

  function addTag() {
    const t = tagInput.trim().slice(0, 20);
    if (t && tags.length < 3 && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput('');
  }

  async function submit() {
    setSubmitting(true);
    const params = {
      mode,
      lang,
      story_type:     storyType,
      audience_style: audience,
      brewer_note:    brewerNote || undefined,
      brewer_tags:    tags,
      visibility,
      is_vhm:         isVhm,
    };
    if (mode === 'coordinate' && coordInfo) {
      params.target_lat = coordInfo.lat;
      params.target_lon = coordInfo.lng;
    }
    onSubmit(params);
  }

  return (
    <div className="brew-flow-panel bfp-params" role="dialog" aria-label="Brew 参数配置">
      <div className="bfp-header">
        <span className="bfp-mode-label">
          {mode === 'random' ? '✦ 随机探索酿造' : '📍 精准坐标酿造'}
        </span>
        {coordInfo && (
          <div className="bfp-coord-badge">
            <span className="bfpb-name">{coordInfo.name || '未知地点'}</span>
            <span className="bfpb-latlon">{coordInfo.lat?.toFixed(2)}°, {coordInfo.lng?.toFixed(2)}°</span>
          </div>
        )}
        <button className="bfp-close" onClick={onCancel} aria-label="取消">✕</button>
      </div>

      <div className="bfp-body">
        {/* Story type */}
        <div className="bfp-section">
          <div className="bfp-section-label">故事类型</div>
          <div className="bfp-type-grid">
            {STORY_TYPES.map(t => (
              <button key={t.id}
                className={`bfp-type-chip ${storyType === t.id ? 'active' : ''}`}
                onClick={() => setStoryType(t.id)}>
                <span className="chip-icon">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Audience — hide for simple users */}
        {!isSimple && (
          <div className="bfp-section">
            <div className="bfp-section-label">受众风格</div>
            <div className="bfp-row-chips">
              {AUDIENCE_STYLES.map(a => (
                <button key={a.id}
                  className={`bfp-row-chip ${audience === a.id ? 'active' : ''}`}
                  onClick={() => setAudience(a.id)}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Language */}
        <div className="bfp-section bfp-section--inline">
          <div className="bfp-section-label">语言</div>
          <div className="bfp-row-chips">
            {LANGS.map(l => (
              <button key={l.id}
                className={`bfp-row-chip ${lang === l.id ? 'active' : ''}`}
                onClick={() => setLang(l.id)}>
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tags — show for P1+ */}
        {!isSimple && (
          <div className="bfp-section">
            <div className="bfp-section-label">
              兴趣标签 <span className="bfp-hint">（选填，最多 3 个）</span>
            </div>
            <div className="bfp-tags">
              {tags.map(t => (
                <span key={t} className="bfp-tag">
                  #{t}
                  <button onClick={() => setTags(tags.filter(x => x !== t))} aria-label="移除标签">✕</button>
                </span>
              ))}
              {tags.length < 3 && (
                <div className="bfp-tag-input-wrap">
                  <input
                    className="bfp-tag-input"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addTag()}
                    placeholder="输入后回车添加"
                    maxLength={20}
                  />
                  {tagInput.trim() && (
                    <button className="bfp-tag-add" onClick={addTag}>+</button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Advanced options toggle */}
        {!isSimple && (
          <button className="bfp-advanced-toggle"
            onClick={() => setShowAdvanced(v => !v)}>
            高级选项 {showAdvanced ? '▲' : '▼'}
          </button>
        )}

        {showAdvanced && (
          <div className="bfp-advanced">
            {/* Brewer note */}
            <div className="bfp-section">
              <div className="bfp-section-label">
                酿造理由 <span className="bfp-hint">（选填，≤100字）</span>
              </div>
              <textarea
                className="bfp-note"
                rows={3}
                maxLength={100}
                value={brewerNote}
                onChange={e => setBrewerNote(e.target.value)}
                placeholder="为什么你选择了这里？"
              />
              <div className="bfp-note-count">{brewerNote.length}/100</div>
            </div>

            {/* Visibility */}
            <div className="bfp-section">
              <div className="bfp-section-label">内容可见性</div>
              <div className="bfp-row-chips">
                {[
                  { id:'private',   label:'仅自己' },
                  { id:'community', label:'社区可见' },
                  { id:'public',    label:'公开' },
                ].map(v => (
                  <button key={v.id}
                    className={`bfp-row-chip ${visibility === v.id ? 'active' : ''}`}
                    onClick={() => setVisibility(v.id)}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* VHM */}
            {canVhm && (
              <label className={`bfp-vhm-toggle ${isVhm ? 'active' : ''}`}>
                <input type="checkbox" checked={isVhm}
                  onChange={e => setIsVhm(e.target.checked)} />
                <span className="vhm-label">
                  <span className="vhm-title">VHM 历史核查模式</span>
                  <span className="vhm-cost">消耗 3⬡</span>
                </span>
                <span className="vhm-desc">基于真实史料核查，内容标注[事实]依据</span>
              </label>
            )}
          </div>
        )}
      </div>

      <div className="bfp-footer">
        <div className="bfp-cost-badge">消耗 {brewCost} ⬡</div>
        <button className="bfp-submit" onClick={submit} disabled={submitting}>
          {submitting ? (
            <span className="bfp-submitting">
              <span className="brew-spinner"></span> 发起酿造…
            </span>
          ) : (
            <>
              <BrewIcon size={13} color="#08100c" />
              开始酿造
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Waiting / Progress Panel ─────────────────────────────────────────
function BrewWaitPanel({ brewId, status, onCancel, placeName }) {
  const [ambientIdx, setAmbientIdx] = useBF(0);
  const stageInfo = STAGE_LABELS[status] || STAGE_LABELS['queued'];
  const ambients  = STAGE_AMBIENT[status] || STAGE_AMBIENT['discovering'];

  // Cycle ambient hints every 7s
  useEBF(() => {
    const t = setInterval(() => setAmbientIdx(i => (i + 1) % ambients.length), 7000);
    return () => clearInterval(t);
  }, [ambients]);

  const steps = [
    { key: 'discovering', label: '发现目标地点'   },
    { key: 'mining',      label: '挖掘文化内容'   },
    { key: 'generating',  label: '撰写故事'        },
    { key: 'review',      label: '内容审核'        },
  ];

  function stepState(key) {
    const order = ['queued','discovering','mining','generating','review'];
    const cur   = order.indexOf(status);
    const me    = order.indexOf(key);
    if (me < 0) return 'idle';
    if (cur > me) return 'done';
    if (cur === me) return 'active';
    return 'idle';
  }

  return (
    <div className="brew-flow-panel bfp-wait" aria-live="polite" aria-label="酿造进行中">
      <div className="bfw-title">✦ 正在酿造你的故事…</div>

      {/* Progress steps */}
      <div className="bfw-steps">
        {steps.map(s => {
          const st = stepState(s.key);
          return (
            <div key={s.key} className={`bfw-step bfw-step--${st}`}>
              <span className="bfws-dot">
                {st === 'done' ? '✓' : st === 'active' ? '◐' : '○'}
              </span>
              <span className="bfws-label">{s.label}</span>
              {st === 'active' && <span className="bfws-running">进行中…</span>}
            </div>
          );
        })}
      </div>

      {/* Place discovery hint */}
      {placeName && (
        <div className="bfw-place-hint">
          <span className="bfwph-dot">📍</span>
          <span>{placeName}</span>
        </div>
      )}

      {/* Ambient info stream */}
      <div className="bfw-ambient" key={ambientIdx}>
        {ambients[ambientIdx]}
      </div>

      {/* Progress bar */}
      <div className="bfw-progress-track">
        <div className="bfw-progress-fill"
          style={{ width: `${(stageInfo.stage / stageInfo.total) * 100}%` }}>
        </div>
      </div>

      <div className="bfw-footer">
        <span className="bfw-est">预计 40-75 秒完成</span>
        <button className="bfw-cancel" onClick={onCancel}>
          取消 <span className="bfw-refund">（退还 50% 点数）</span>
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Reveal Panel — 「开箱时刻」 ────────────────────────────────────────
function BrewRevealPanel({ brewResult, culturalCircle, onPlay, onAnnotate, onClose }) {
  const [revealed, setRevealed] = useBF(false);
  const [note,     setNote]     = useBF('');
  const [tags,     setTags]     = useBF([]);
  const [tagInput, setTagInput] = useBF('');
  const [vis,      setVis]      = useBF('public');
  const [saving,   setSaving]   = useBF(false);
  const [saved,    setSaved]    = useBF(false);

  // Trigger reveal animation after a brief delay
  useEBF(() => {
    const t = setTimeout(() => setRevealed(true), 120);
    return () => clearTimeout(t);
  }, []);

  const cultureColor = (window.CULTURES && culturalCircle)
    ? CULTURES[culturalCircle]?.color || '#c8973a'
    : '#c8973a';

  function addTag() {
    const t = tagInput.trim().slice(0, 20);
    if (t && tags.length < 3 && !tags.includes(t)) setTags([...tags, t]);
    setTagInput('');
  }

  async function saveAnnotation() {
    setSaving(true);
    try {
      await BrewAPI.annotate(brewResult.brew_id, {
        brewerNote: note,
        brewerTags: tags,
        visibility: vis,
      });
      BrewCreditsCache.invalidate();
      setSaved(true);
      setTimeout(() => onClose && onClose(), 1200);
    } catch (e) {
      console.warn('[BrewFlow] annotate failed', e.message);
      setSaving(false);
    }
  }

  return (
    <div className={`brew-flow-panel bfp-reveal ${revealed ? 'revealed' : ''}`}
      aria-label="故事酿造完成">

      {/* Header shimmer */}
      <div className="bfr-header">
        <div className="bfr-celebration">✦ 你的故事酿造完成</div>
      </div>

      {/* Story card */}
      <div className="bfr-card" style={{
        '--culture-color': cultureColor,
        background: `radial-gradient(ellipse at 50% 0%, ${cultureColor}12 0%, transparent 65%)`,
      }}>
        <div className="bfr-card-divider"></div>
        <h2 className="bfr-title">{brewResult.story_title || '故事已生成'}</h2>
        <div className="bfr-card-divider"></div>

        <div className="bfr-meta-row">
          <span className="bfr-place">📍 {brewResult.resolved_place_name || '未知地点'}</span>
        </div>
        {brewResult.story_excerpt && (
          <blockquote className="bfr-excerpt">
            「{brewResult.story_excerpt}」
          </blockquote>
        )}

        {/* Play CTA */}
        <button className="bfr-play-btn" onClick={() => onPlay && onPlay(brewResult)}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
            <path d="M7 4 L20 12 L7 20 Z" fill="currentColor"/>
          </svg>
          立即收听
          {brewResult.story_duration && (
            <span className="bfr-duration">[{brewResult.story_duration}]</span>
          )}
        </button>
      </div>

      {/* Sovereignty annotation section */}
      <div className="bfr-annotation">
        <div className="bfra-head">
          这是你的故事 — 留下你的印记
        </div>

        {/* Brewer note */}
        <textarea
          className="bfra-note"
          rows={2}
          maxLength={100}
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="为何酿造这个？（选填）"
        />

        {/* Tags */}
        <div className="bfra-tags">
          {tags.map(t => (
            <span key={t} className="bfra-tag">
              #{t}
              <button onClick={() => setTags(tags.filter(x => x !== t))}>✕</button>
            </span>
          ))}
          {tags.length < 3 && (
            <div className="bfra-tag-add-wrap">
              <input
                className="bfra-tag-input"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTag()}
                placeholder={`# 添加标签 ${tags.length}/3`}
                maxLength={20}
              />
            </div>
          )}
        </div>

        {/* Visibility */}
        <div className="bfra-vis-row">
          {[
            { id:'private',   icon:'⊙', label:'仅自己' },
            { id:'community', icon:'◐', label:'社区'   },
            { id:'public',    icon:'○', label:'公开'   },
          ].map(v => (
            <label key={v.id} className={`bfra-vis-opt ${vis === v.id ? 'active' : ''}`}>
              <input type="radio" name="visibility" value={v.id}
                checked={vis === v.id} onChange={() => setVis(v.id)} />
              {v.icon} {v.label}
            </label>
          ))}
        </div>

        <button className={`bfra-save ${saved ? 'saved' : ''}`}
          onClick={saveAnnotation} disabled={saving || saved}>
          {saved
            ? '✓ 已保存'
            : saving
              ? <><span className="brew-spinner"></span> 保存中…</>
              : '保存主权标注'}
        </button>

        <button className="bfra-skip" onClick={onClose}>稍后设置</button>
      </div>
    </div>
  );
}

// ── Achievement Toast ─────────────────────────────────────────────────────────
function BrewAchievement({ title, desc, onDone }) {
  useEBF(() => {
    const t = setTimeout(() => onDone && onDone(), 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="brew-achievement">
      <div className="ba-icon">⭐</div>
      <div className="ba-text">
        <div className="ba-title">{title}</div>
        <div className="ba-desc">{desc}</div>
      </div>
    </div>
  );
}

// ── BrewCoordPicker — full-screen globe coord selection overlay ────────────────
function BrewCoordPicker({ globeRef, onPick, onCancel, userTier }) {
  const [picked, setPicked] = useBF(null);

  const precisionLabel = {
    F:  null,
    I:  '省级精度 · 100km 范围内寻找',
    P1: '城市级精度 · 15km 范围内寻找',
    P2: '街区级精度 · 3km 范围内寻找',
    X:  '精确点位 · 50m 精度',
  }[userTier];

  // When globeRef fires onSelectCoord, we capture it
  useEBF(() => {
    const orig = globeRef?.current?._pickCallback;
    if (globeRef?.current) {
      globeRef.current._pickCallback = (coord) => {
        setPicked(coord);
      };
    }
    return () => {
      if (globeRef?.current) globeRef.current._pickCallback = orig;
    };
  }, [globeRef]);

  return (
    <div className="brew-coord-picker" role="dialog" aria-label="选择坐标">
      <div className="bcp-top-bar">
        <span className="bcp-hint">在地球仪上点击你想酿造故事的地方</span>
        <button className="bcp-cancel" onClick={onCancel}>✕ 取消</button>
      </div>
      {precisionLabel && (
        <div className="bcp-precision">{precisionLabel}</div>
      )}
      {picked && (
        <div className="bcp-picked">
          <div className="bcp-picked-pulse"></div>
          <div className="bcp-picked-info">
            <span className="bcp-picked-name">{picked.name || `${picked.lat?.toFixed(2)}°, ${picked.lng?.toFixed(2)}°`}</span>
            {picked.region && <span className="bcp-picked-region">{picked.region}</span>}
          </div>
          <button className="bcp-confirm" onClick={() => onPick(picked)}>
            在此酿造 →
          </button>
        </div>
      )}
    </div>
  );
}

// ── InsufficientCredits modal ─────────────────────────────────────────────────
function BrewInsufficientCredits({ onClose, onUpgrade }) {
  return (
    <div className="brew-flow-panel bfp-insufficient" role="alertdialog" aria-label="点数不足">
      <div className="bfi-icon">⬡</div>
      <div className="bfi-title">点数不足</div>
      <div className="bfi-body">
        <p>你的余额不足以发起酿造。</p>
        <div className="bfi-options">
          <div className="bfi-opt">
            <span className="bfio-icon">◎</span>
            完成今天的聆听目标（+1⬡）
          </div>
          <button className="bfi-upgrade-btn" onClick={onUpgrade}>
            升级到 Pro，立享 25⬡/月 →
          </button>
        </div>
      </div>
      <button className="bfp-close bfp-close--sm" onClick={onClose}>关闭</button>
    </div>
  );
}

// ── BrewFlow orchestrator ─────────────────────────────────────────────────────
// step: 'panel' | 'coord-pick' | 'params' | 'waiting' | 'reveal' | 'credits' | null
//
// Props:
//   initialMode: 'random' | 'coordinate' | null
//   coordInfo:   { lat, lng, name, culture } | null  — pre-filled from globe long-press
//   globeRef:    React ref to GlobeScene
//   userTier:    'F'|'I'|'P1'|'P2'|'X'
//   onClose:     () => void
//   onPlayBrew:  (brewResult) => void  — launches the audio player for the brewed story

function BrewFlow({ initialMode, coordInfo: initialCoord, globeRef, userTier = 'F', onClose, onPlayBrew }) {
  const [step,       setStep]     = useBF(() => initialMode ? 'params' : 'panel');
  const [mode,       setMode]     = useBF(initialMode || 'random');
  const [coordInfo,  setCoordInfo] = useBF(initialCoord || null);
  const [brewId,     setBrewId]   = useBF(null);
  const [brewStatus, setBrewStatus] = useBF(null); // full status object
  const [brewResult, setBrewResult] = useBF(null); // terminal status with story data
  const [achievement, setAchiev]  = useBF(null);
  const [credError,  setCredErr]  = useBF(null);
  const pollerRef                  = useRBF(null);

  // Cleanup poller on unmount
  useEBF(() => () => pollerRef.current?.stop(), []);

  // ── Transition: panel → params ───────────────────────────────────────────
  function handlePanelStart(cfg) {
    if (cfg.insufficientCredits) { setStep('credits'); return; }
    if (cfg.mode === 'coordinate') {
      if (!coordInfo) { setMode('coordinate'); setStep('coord-pick'); return; }
    }
    setMode(cfg.mode || 'random');
    setStep('params');
  }

  // ── Coord picked from globe ──────────────────────────────────────────────
  function handleCoordPick(coord) {
    setCoordInfo(coord);
    setMode('coordinate');
    setStep('params');
  }

  // ── Params submitted → call API ──────────────────────────────────────────
  async function handleParamsSubmit(params) {
    setStep('waiting');
    try {
      const res  = await BrewAPI.initiate(params);
      const id   = res.brew_id;
      setBrewId(id);
      console.info('[BrewFlow] initiated', id);

      // Start polling
      pollerRef.current = new BrewPoller(
        id,
        (s) => setBrewStatus(s),
        (s) => {
          pollerRef.current = null;
          if (s.status === 'approved') {
            setBrewResult(s);
            setStep('reveal');
            BrewCreditsCache.invalidate();
            // Check first-brew achievement
            BrewAPI.getHistory({ pageSize: 2 }).then(h => {
              const arr = Array.isArray(h) ? h : (h?.items ?? []);
              if (arr.filter(x => x.status === 'approved').length === 1) {
                setAchiev({ title: '初酿者', desc: '完成了第一次酿造！' });
              }
            }).catch(() => {});
          } else {
            // rejected / failed
            setBrewResult(s);
            setStep('reveal');
          }
        },
        (err) => {
          console.warn('[BrewFlow] poll error', err.message);
          // non-fatal: keep waiting UI, poller continues
        },
      );
      pollerRef.current.start();
    } catch (e) {
      console.error('[BrewFlow] initiate failed', e);
      setCredErr(e.code === 'INSUFFICIENT_CREDITS'
        ? 'insufficient'
        : e.message || '网络错误，请稍后重试');
      setStep('error');
    }
  }

  // ── Cancel waiting ───────────────────────────────────────────────────────
  function handleCancelWait() {
    pollerRef.current?.stop();
    pollerRef.current = null;
    // Optimistic close — refund is handled server-side
    onClose && onClose();
  }

  // ── Play brewed story ────────────────────────────────────────────────────
  function handlePlayBrew(result) {
    onPlayBrew && onPlayBrew(result);
    onClose && onClose();
  }

  // ── Resolve current place name from status ───────────────────────────────
  const placeName = useMBF(() =>
    brewStatus?.resolved_place_name || coordInfo?.name || null,
  [brewStatus, coordInfo]);

  const culturalCircle = useMBF(() =>
    coordInfo?.culture || null,
  [coordInfo]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="brew-flow-overlay" onClick={e => e.target === e.currentTarget && onClose?.()}>

      {/* Achievement toast */}
      {achievement && (
        <BrewAchievement
          title={achievement.title}
          desc={achievement.desc}
          onDone={() => setAchiev(null)}
        />
      )}

      {/* Step: panel */}
      {step === 'panel' && (
        <BrewPanel
          userTier={userTier}
          onClose={onClose}
          onStartBrew={handlePanelStart}
        />
      )}

      {/* Step: coord-pick */}
      {step === 'coord-pick' && (
        <BrewCoordPicker
          globeRef={globeRef}
          userTier={userTier}
          onPick={handleCoordPick}
          onCancel={() => setStep('panel')}
        />
      )}

      {/* Step: params */}
      {step === 'params' && (
        <BrewParamPanel
          mode={mode}
          coordInfo={coordInfo}
          userTier={userTier}
          onSubmit={handleParamsSubmit}
          onCancel={() => setStep(initialMode ? null : 'panel')}
        />
      )}

      {/* Step: waiting */}
      {step === 'waiting' && (
        <BrewWaitPanel
          brewId={brewId}
          status={brewStatus?.status || 'queued'}
          placeName={placeName}
          onCancel={handleCancelWait}
        />
      )}

      {/* Step: reveal */}
      {step === 'reveal' && brewResult && (
        brewResult.status === 'approved' ? (
          <BrewRevealPanel
            brewResult={brewResult}
            culturalCircle={culturalCircle}
            onPlay={handlePlayBrew}
            onClose={onClose}
          />
        ) : (
          // Rejected / failed state
          <div className="brew-flow-panel bfp-error">
            <div className="bfe-icon">⚠</div>
            <div className="bfe-title">
              {brewResult.status === 'rejected' ? '内容未通过审核' : '酿造遭遇了意外'}
            </div>
            <div className="bfe-body">
              {brewResult.status === 'rejected'
                ? '此次生成内容未满足质量标准。如有疑问可在历史记录中申诉。'
                : '这不是你的问题。消耗的点数将自动退回。'}
            </div>
            <button className="bfe-close" onClick={onClose}>返回</button>
          </div>
        )
      )}

      {/* Step: credits insufficient */}
      {step === 'credits' && (
        <BrewInsufficientCredits
          onClose={() => setStep('panel')}
          onUpgrade={() => { onClose?.(); /* route to upgrade */ }}
        />
      )}

      {/* Step: error */}
      {step === 'error' && (
        <div className="brew-flow-panel bfp-error">
          <div className="bfe-icon">⚠</div>
          <div className="bfe-title">
            {credError === 'insufficient' ? '点数不足' : '连接失败'}
          </div>
          <div className="bfe-body">
            {credError === 'insufficient'
              ? '余额不足以发起酿造。'
              : credError || '请检查网络后重试。'}
          </div>
          <button className="bfe-retry" onClick={() => setStep('params')}>重试</button>
          <button className="bfe-close" onClick={onClose}>取消</button>
        </div>
      )}
    </div>
  );
}

window.BrewFlow = BrewFlow;
